import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Eye, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight,
  Play
} from 'lucide-react';

const VirtualTourSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const images = [
    {
      url: "https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/ohk6kkgs_Chambre%20Principale.jpg",
      title: "Chambre Principale",
      description: "Suite parentale avec dressing intégré"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/z4eaard8_Cuisine%20%282%29.jpg",
      title: "Cuisine Moderne",
      description: "Équipée avec électroménager haut de gamme"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/ypt3cxvw_Chambre%202%281%29.jpg",
      title: "Chambre Secondaire",
      description: "Chambre spacieuse avec rangements"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/hgvzuzwl_Chambre%203.jpg",
      title: "Chambre d'Invités",
      description: "Confort et intimité pour vos invités"
    }
  ];

  const openVirtualTour = () => {
    window.open('https://vr.shinewonder.com/pano/page/publik/pklimit?inf=@DBFFDBMMFBBEFDA@', '_blank');
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section id="virtual-tour" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-amber-100 text-amber-800 border-amber-200">
            <Eye className="h-4 w-4 mr-2" />
            Visite Virtuelle & Médias
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Explorez votre appartement
            <br />
            <span className="text-amber-600">comme si vous y étiez</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-12">
            Découvrez chaque détail de votre futur chez-vous grâce à notre visite virtuelle 360° 
            et notre galerie d'images haute définition.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Virtual Tour CTA */}
          <div>
            <Card className="bg-gradient-to-br from-blue-900 to-slate-800 text-white overflow-hidden">
              <CardContent className="p-8 md:p-12 text-center">
                <div className="w-20 h-20 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Play className="h-10 w-10 text-white ml-1" />
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Visite Virtuelle 360°
                </h3>
                
                <p className="text-slate-200 mb-8 text-lg">
                  Plongez dans une expérience immersive et visitez l'appartement 
                  dans les moindres détails, à votre rythme.
                </p>
                
                <Button 
                  onClick={openVirtualTour}
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold"
                >
                  <ExternalLink className="h-5 w-5 mr-2" />
                  Lancer la visite 360°
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Image Slider */}
          <div className="relative">
            <Card className="overflow-hidden shadow-2xl">
              <CardContent className="p-0">
                <div className="relative h-96 lg:h-[500px]">
                  <img 
                    src={images[currentImage].url}
                    alt={images[currentImage].title}
                    className="w-full h-full object-cover transition-all duration-500"
                  />
                  
                  {/* Navigation buttons */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
                  >
                    <ChevronLeft className="h-6 w-6 text-slate-800" />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
                  >
                    <ChevronRight className="h-6 w-6 text-slate-800" />
                  </button>

                  {/* Image info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <h4 className="text-xl font-bold text-white mb-1">
                      {images[currentImage].title}
                    </h4>
                    <p className="text-slate-200">
                      {images[currentImage].description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Image indicators */}
            <div className="flex justify-center space-x-2 mt-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentImage 
                      ? 'bg-amber-600 w-8' 
                      : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-2 mt-6">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`relative h-20 rounded-lg overflow-hidden transition-all duration-300 ${
                    index === currentImage 
                      ? 'ring-3 ring-amber-600 ring-offset-2' 
                      : 'hover:opacity-75'
                  }`}
                >
                  <img 
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VirtualTourSection;